import {
  Container, Row, Card
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,

  Divider
} from "@whitespace/whitespace-ui";
import useConfig from "hooks/useConfig";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { parseUsingParseInt } from "helpers/stringUtils";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import IWorkOrderStatusViewModel from "interfaces/IWorkOrderStatusViewModel";
import IGetWorkOrderStatusesResponseModel from "interfaces/Response/IGetWorkOrderStatusesResponseModel";
import SiteAndRegionTemplate from "./Templates/SiteAndRegionTemplate";
import { mapContainerStatusesToContainerViewStatusModels } from "./DataMappers/WorkOrderStatusMapper";
import ContainerTemplate from "./Templates/ContainerTemplate";
import LastUpdatedTemplate from "./Templates/LastUpdatedTemplate";
import AllocationTemplate from "./Templates/AllocationTemplate";
import WorkOrderReferenceTemplate from "./Templates/WorkOrderReferenceTemplate";
import DueDateTemplate from "./Templates/DueDateTemplate";
import StatusTemplate from "./Templates/StatusTemplate";

import "styles/sf-datagrid.scss";
import "./WorkOrderStatuses.scss";

const config = useConfig();

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const WorkOrderStatuses = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);
  const [refreshWorkOrderStatuses, setRefreshWorkOrderStatuses] = useState(false);

  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();
  const [workOrderViewModels, setWorkOrderViewModels] = useState<IWorkOrderStatusViewModel[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("workorders/statuses", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (refreshWorkOrderStatuses) {
      sendRequest("workorders/statuses", "GET");
      setRefreshWorkOrderStatuses(false);
    }
  }, [sendRequest, refreshWorkOrderStatuses]);

  useEffect(() => {
    if (data && data.workOrders) {
      const response = data as IGetWorkOrderStatusesResponseModel;
      const mappings = mapContainerStatusesToContainerViewStatusModels(response.workOrders);
      setWorkOrderViewModels(mappings);
    }
  }, [data, setWorkOrderViewModels]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  useEffect(() => {
    if (config.workOrderStatusRefreshMilliseconds) {
      setInterval(() => {
        setRefreshWorkOrderStatuses(true);
      }, config.workOrderStatusRefreshMilliseconds);
    }
  }, [config.workOrderStatusRefreshMilliseconds]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const siteAndRegionTemplateInstance = SiteAndRegionTemplate;
  const containerTemplateInstance = ContainerTemplate;
  const statusTemplateInstance = StatusTemplate;
  const lastUpdatedTemplateInstance = LastUpdatedTemplate;
  const allocationTemplateInstance = AllocationTemplate;
  const dueDateTemplateInstance = DueDateTemplate;
  const workOrderReferenceTemplateInstance = WorkOrderReferenceTemplate;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Work Order Details
              <Spinner
                animation="grow"
                visible={isSending || !renderingComplete}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <section className="work-order-statuses pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section">
                    <GridComponent
                      ref={(g: GridComponent) => {
                        grid = g;
                      }}
                      id="workorderstatusesgrid"
                      data-testid="gridWorkOrderStatuses"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      enablePersistence
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={workOrderViewModels}
                      actionComplete={() => {
                        makeGridAACompliant(grid!, () => {
                          setRenderingComplete(true);
                        });
                      }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="regionHwrcSiteSortOrder"
                          headerText="Site and Region"
                          template={siteAndRegionTemplateInstance}
                        />
                        <ColumnDirective
                          field="containerWasteType"
                          headerText="Container"
                          template={containerTemplateInstance}
                        />
                        <ColumnDirective
                          field="status"
                          headerText="Status"
                          template={statusTemplateInstance}
                          width={130}
                        />
                        <ColumnDirective
                          field="lastUpdatedTimestamp"
                          headerText="Last Updated"
                          type="date"
                          template={lastUpdatedTemplateInstance}
                          width={130}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          field="collectorName"
                          headerText="Allocation"
                          template={allocationTemplateInstance}
                          hideAtMedia="(min-width:1400px)"
                        />
                        <ColumnDirective
                          field="slaEnd"
                          headerText="Due"
                          template={dueDateTemplateInstance}
                          type="date"
                          width={130}
                        />
                        <ColumnDirective
                          field="disposalSiteName"
                          headerText="Disposal Site"
                          defaultValue="not sure"
                          hideAtMedia="(min-width:1400px)"
                        />
                        <ColumnDirective
                          field="workOrderReference"
                          data-testid="workOrderReference"
                          headerText="Reference"
                          headerTextAlign="Center"
                          template={workOrderReferenceTemplateInstance}
                          width={120}
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default WorkOrderStatuses;
